import { useState, useEffect } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Checkbox,
  TextField,
  Button,
  Banner,
  Badge,
  Collapsible,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../lib/api';
import { ECOMMERCE_EVENTS } from '@analyzely/shared';

interface EcommerceEventsProps {
  siteId: string;
  isConfigured: boolean;
}

export function EcommerceEvents({ siteId, isConfigured }: EcommerceEventsProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [apiSecret, setApiSecret] = useState('');
  const [hasApiSecret, setHasApiSecret] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean | string>(false);

  // Load events configuration
  useEffect(() => {
    if (isConfigured && isExpanded) {
      loadEvents();
    }
  }, [siteId, isConfigured, isExpanded]);

  const loadEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await analyticsApi.getEvents(siteId);
      setEvents(response.data.events);
      setHasApiSecret(response.data.has_api_secret);
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Failed to load events configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventToggle = (eventId: string) => {
    setEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Only send api_secret if it's been entered (not empty)
      const response = await analyticsApi.updateEvents(
        siteId,
        events,
        apiSecret || undefined
      );
      setHasApiSecret(response.data.has_api_secret);
      setApiSecret(''); // Clear the input

      // Show success message from API (includes auto-config status)
      const message = response.data.secret_auto_created
        ? 'Events saved! API secret auto-configured.'
        : 'E-commerce events saved successfully!';
      setSuccess(message);
      setTimeout(() => setSuccess(false), 4000);

      // Invalidate queries so Reports page gets fresh data
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', siteId] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    } catch (err) {
      console.error('Failed to save events:', err);
      setError('Failed to save events configuration');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isConfigured) {
    return null;
  }

  const enabledCount = events.length;
  const purchaseEnabled = events.includes('purchase');

  return (
    <Card>
      <BlockStack gap="400">
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  E-commerce Event Tracking
                </Text>
                {enabledCount > 0 && (
                  <Badge tone="info">{`${enabledCount} enabled`}</Badge>
                )}
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Track e-commerce events like add to cart, checkout, and purchases
              </Text>
            </BlockStack>
            <Button
              icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
              variant="plain"
              accessibilityLabel={isExpanded ? 'Collapse' : 'Expand'}
            />
          </InlineStack>
        </div>

        <Collapsible open={isExpanded} id="ecommerce-events-collapsible">
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}

            {success && (
              <Banner tone="success" onDismiss={() => setSuccess(false)}>
                {typeof success === 'string' ? success : 'E-commerce events saved successfully!'}
              </Banner>
            )}

            {isLoading ? (
              <Text as="p" tone="subdued">Loading events...</Text>
            ) : (
              <>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    Client-side Events
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    These events are tracked automatically using JavaScript on your site.
                  </Text>
                  {ECOMMERCE_EVENTS.filter((e) => e.id !== 'purchase').map((event) => (
                    <Checkbox
                      key={event.id}
                      label={event.label}
                      helpText={event.description}
                      checked={events.includes(event.id)}
                      onChange={() => handleEventToggle(event.id)}
                    />
                  ))}
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    Server-side Events
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    These events are tracked via webhooks from Webflow.
                  </Text>
                  <Checkbox
                    label="Purchase"
                    helpText="Track completed purchases via Webflow webhook"
                    checked={purchaseEnabled}
                    onChange={() => handleEventToggle('purchase')}
                  />

                  {purchaseEnabled && (
                    <BlockStack gap="200">
                      {hasApiSecret ? (
                        <Banner tone="success">
                          API secret is configured. Purchase tracking is ready!
                        </Banner>
                      ) : (
                        <Banner tone="info">
                          API secret will be auto-configured when you save.
                          Or enter one manually below to override.
                        </Banner>
                      )}
                      <TextField
                        label="GA4 API Secret (Optional)"
                        value={apiSecret}
                        onChange={setApiSecret}
                        placeholder={hasApiSecret ? 'API secret configured (enter new to update)' : 'Leave empty to auto-configure'}
                        helpText={hasApiSecret ? 'A secret is already configured. Enter a new one to update it.' : 'Leave empty for automatic configuration, or enter your own secret'}
                        autoComplete="off"
                      />
                    </BlockStack>
                  )}
                </BlockStack>

                <InlineStack gap="200">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    loading={isSaving}
                  >
                    Save Events
                  </Button>
                </InlineStack>

                <Banner>
                  <p>
                    <strong>Note:</strong> After saving, you need to re-apply the tracking code
                    and publish your Webflow site for changes to take effect.
                  </p>
                </Banner>
              </>
            )}
          </BlockStack>
        </Collapsible>
      </BlockStack>
    </Card>
  );
}
